import React, { useState, useRef, useEffect } from 'react';
import { capitalizeRelationship, SINGLETON_RELATIONS } from '../../utils/subscriberHelpers';
import AddFamilyMemberModal from './AddFamilyMemberModal';
import EditFamilyMemberModal from './EditFamilyMemberModal';

/**
 * Props:
 *   members          — array of { id, name, relationship, mobile, gender, age, email }
 *   loading          — bool, show loading state
 *   value            — selected member id
 *   onChange(id)     — called on selection
 *   onAddMember(data)               — async, called when add form submits
 *   onEditMember(memberId, data)    — async, called when edit form submits
 *   addingMember     — bool, loading state for add
 *   savingMember     — bool, loading state for edit
 *   error            — string, validation error
 *   mounted          — bool, for modal portals
 */
const PatientSelector = ({
  members = [],
  loading = false,
  value = '',
  onChange,
  onAddMember,
  onEditMember,
  addingMember = false,
  savingMember = false,
  error,
  mounted = false,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);

  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        triggerRef.current && dropdownRef.current &&
        !triggerRef.current.contains(e.target) &&
        !dropdownRef.current.contains(e.target)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAddSubmit = async (memberData) => {
    const success = await onAddMember?.(memberData);
    if (success) setShowAddModal(false);
  };

  const handleEditSubmit = async (memberId, memberData) => {
    const success = await onEditMember?.(memberId, memberData);
    if (success) setEditingMember(null);
  };

  const selected = members.find(m => m.id === value);

  const singletonRelations = new Set(
    members
      .filter(m => SINGLETON_RELATIONS.includes(m.relationship?.toLowerCase()))
      .map(m => m.relationship?.toLowerCase())
  );

  const editSingletonRelations = new Set(
    members
      .filter(m => m.id !== editingMember?.id && SINGLETON_RELATIONS.includes(m.relationship?.toLowerCase()))
      .map(m => m.relationship?.toLowerCase())
  );

  return (
    <>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Patient <span className="text-red-500">*</span>
        </label>

        <div className="flex gap-2">
          {/* Dropdown trigger */}
          <div className="relative flex-1" ref={triggerRef}>
            <button
              type="button"
              onClick={() => { if (!loading) setShowDropdown(prev => !prev); }}
              disabled={loading}
              className={`w-full px-4 py-2.5 border rounded-md text-left flex items-center justify-between focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${error ? 'border-red-500' : 'border-gray-300'} ${loading ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}`}
            >
              {selected ? (
                <div className="flex items-center gap-2.5 min-w-0">
                  <span className="font-medium text-gray-900 truncate capitalize">{selected.name}</span>
                  <span className="text-xs text-gray-400 flex-shrink-0">({capitalizeRelationship(selected.relationship)})</span>
                </div>
              ) : (
                <span className="text-gray-400">
                  {loading ? 'Loading members...' : 'Select patient...'}
                </span>
              )}
              <svg
                className={`w-4 h-4 text-gray-400 flex-shrink-0 ml-2 transition-transform duration-200 ${showDropdown ? 'rotate-180' : ''}`}
                fill="none" viewBox="0 0 24 24" stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {/* Dropdown list */}
            {showDropdown && members.length > 0 && (
              <div
                ref={dropdownRef}
                className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden"
                style={{ maxHeight: '280px', overflowY: 'auto' }}
              >
                <div className="px-3 py-2 bg-gray-50 border-b border-gray-100">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Select Patient</p>
                </div>

                {members.map((m) => {
                  const isSelected = value === m.id;
                  return (
                    <div
                      key={m.id}
                      onClick={() => {
                        onChange?.(m.id);
                        setShowDropdown(false);
                      }}
                      className={`flex items-center gap-3 px-4 py-3 cursor-pointer border-b border-gray-200 last:border-b-0 transition-all duration-150 group ${isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                    >
                      {/* Avatar */}
                      {/* <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${avatarColor}`}>
                        {initials}
                      </div> */}

                      <div className="flex-1 min-w-0">
                        {/* Name row */}
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-gray-900 text-sm truncate capitalize">
                            {m.name.charAt(0).toUpperCase() + m.name.slice(1)}
                          </span>
                          {isSelected && (
                            <svg className="w-4 h-4 text-blue-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                          {/* Edit button */}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingMember(m);
                              setShowDropdown(false);
                            }}
                            className="ml-auto flex-shrink-0 p-1 rounded text-gray-500 hover:text-blue-600 hover:bg-blue-100 transition-all"
                            title="Edit member"
                          >
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                        </div>

                        {/* Badges row */}
                        <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold border bg-violet-50 text-violet-500 border-violet-200">
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                            {capitalizeRelationship(m.relationship)}
                          </span>

                          {m.gender && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold border bg-pink-50 text-pink-500 border-fuchsia-200">
                              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                              {m.gender.charAt(0).toUpperCase() + m.gender.slice(1).toLowerCase()}
                            </span>
                          )}

                          {m.age != null && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold border bg-amber-50 text-amber-500 border-amber-200">
                              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                              {m.age} yrs
                            </span>
                          )}

                          {m.mobile && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold border bg-emerald-50 text-emerald-500 border-emerald-200">
                              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                              {m.mobile}
                            </span>
                          )}

                          {m.email && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold border bg-sky-50 text-sky-500 border-sky-200">
                              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                              {m.email}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Add patient button */}
          <button
            type="button"
            onClick={() => setShowAddModal(true)}
            disabled={addingMember}
            className={`px-4 py-2.5 text-sm font-medium border rounded-md transition-colors whitespace-nowrap ${addingMember ? 'text-gray-400 border-gray-200 cursor-not-allowed' : 'text-blue-600 border-blue-300 hover:bg-blue-50'}`}
          >
            {addingMember ? 'Adding...' : '+ Add Patient'}
          </button>
        </div>

        {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
      </div>

      <AddFamilyMemberModal
        mounted={mounted}
        show={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={handleAddSubmit}
        overlayClassName="bg-black/20"
        size="md"
        existingSingletonRelations={singletonRelations}
        existingMembers={members}
      />

      <EditFamilyMemberModal
        mounted={mounted}
        show={!!editingMember}
        member={editingMember}
        onClose={() => setEditingMember(null)}
        onSubmit={handleEditSubmit}
        existingSingletonRelations={editSingletonRelations}
        existingMembers={members}
      />
    </>
  );
};

export default PatientSelector;
